import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Receipt, Eye, Printer, Download, Search, Trash2, Upload, FileText, CheckCircle, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

const statusLabels = { emise: "Émise", payee: "Payée", annulee: "Annulée" };
const statusColors = {
  emise: "bg-blue-50 text-blue-600", payee: "bg-emerald-50 text-emerald-600", annulee: "bg-slate-100 text-slate-500"
};

export default function FacturesPage() {
  const { apiFetch, API, user } = useAuth();
  const isWebmaster = user?.role === "webmaster";
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importStep, setImportStep] = useState("upload");
  const [importFile, setImportFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const fileInputRef = useRef(null);

  const fetchFactures = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/factures`);
      if (res.ok) setFactures(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiFetch, API]);

  useEffect(() => { fetchFactures(); }, [fetchFactures]);

  const updateStatus = async (factureId, newStatus) => {
    const res = await apiFetch(`${API}/factures/${factureId}/status`, {
      method: "PUT", body: JSON.stringify({ status: newStatus })
    });
    if (res.ok) { toast.success("Statut mis à jour"); fetchFactures(); }
  };

  const handleDownloadPdf = async (factureId, factureNumber) => {
    try {
      const res = await apiFetch(`${API}/factures/${factureId}/pdf`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${factureNumber.replace(/\//g, "-")}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("PDF téléchargé");
      }
    } catch (e) { toast.error("Erreur lors du téléchargement"); }
  };

  const viewFacture = (facture) => { setSelectedFacture(facture); setDialogOpen(true); };

  const handleDeleteFacture = async (factureId) => {
    if (!window.confirm("Confirmez-vous la suppression de cette facture ?")) return;
    const res = await apiFetch(`${API}/factures/${factureId}`, { method: "DELETE" });
    if (res.ok) {
      toast.success("Facture supprimée");
      fetchFactures();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Erreur lors de la suppression");
    }
  };

  const sendFactureEmail = async (factureId) => {
    const res = await apiFetch(`${API}/factures/${factureId}/send-email`, { method: "POST" });
    if (res.ok) {
      const data = await res.json();
      toast.success(data.message);
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Erreur lors de l'envoi");
    }
  };

  // --- Import functions ---
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === "application/pdf") {
      setImportFile(file);
    } else {
      toast.error("Veuillez sélectionner un fichier PDF");
    }
  };

  const handleAnalyze = async () => {
    if (!importFile) return;
    setImportStep("analyzing");
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const token = localStorage.getItem("jvc_token");
      const res = await fetch(`${API}/devis/import`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setExtractedData(data.extracted);
        setImportStep("preview");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur d'analyse");
        setImportStep("upload");
      }
    } catch (e) {
      toast.error("Erreur lors de l'analyse du PDF");
      setImportStep("upload");
    }
  };

  const handleConfirmImport = async () => {
    setImportStep("confirming");
    try {
      const res = await apiFetch(`${API}/devis/import/confirm`, {
        method: "POST",
        body: JSON.stringify({ extracted: extractedData, document_type: "facture" }),
      });
      if (res.ok) {
        const data = await res.json();
        const bcNum = data.commande_number || "";
        const facNum = data.facture_number || "";
        toast.success(`Facture importée avec succès${bcNum ? ` — BC ${bcNum}` : ""}${facNum ? `, Facture ${facNum}` : ""}`);
        setImportDialogOpen(false);
        resetImport();
        fetchFactures();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
        setImportStep("preview");
      }
    } catch (e) {
      toast.error("Erreur d'import");
      setImportStep("preview");
    }
  };

  const resetImport = () => {
    setImportStep("upload");
    setImportFile(null);
    setExtractedData(null);
  };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "-";

  const filtered = factures.filter(f => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return f.facture_number?.toLowerCase().includes(q) ||
           f.client_name?.toLowerCase().includes(q) ||
           f.commande_number?.toLowerCase().includes(q);
  });

  const printFacture = () => {
    const printContent = document.getElementById("facture-print-content");
    if (!printContent) return;
    const w = window.open("", "_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>Facture ${selectedFacture?.facture_number}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: Arial, sans-serif; color: #1e293b; padding: 0; }
        .header { background: #1a3a5c; color: white; padding: 30px; display: flex; justify-content: space-between; align-items: flex-start; }
        .header-left { display: flex; gap: 16px; align-items: center; }
        .header-logo { width: 70px; height: 70px; background: white; border-radius: 8px; padding: 4px; }
        .header-logo img { width: 100%; height: 100%; object-fit: contain; }
        .header h1 { font-size: 20px; font-weight: bold; text-transform: uppercase; }
        .header .subtitle { font-size: 12px; color: #93c5fd; margin-top: 4px; }
        .header .contact { font-size: 11px; color: #93c5fd; margin-top: 8px; }
        .header-right { text-align: right; }
        .header-right h2 { font-size: 28px; font-weight: bold; text-transform: uppercase; }
        .header-right .number { font-size: 16px; color: #bfdbfe; margin-top: 4px; }
        .content { padding: 30px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
        .info-box { background: #f8fafc; padding: 16px; border-radius: 6px; }
        .label { font-size: 10px; color: #94a3b8; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; margin-bottom: 6px; }
        .objet { background: #eff6ff; border: 1px solid #bfdbfe; padding: 12px; border-radius: 6px; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        thead tr { background: #1a3a5c; color: white; }
        th { padding: 10px 12px; font-size: 11px; font-weight: 600; text-align: left; }
        th:nth-child(2), th:nth-child(3), th:nth-child(4) { text-align: right; }
        td { padding: 10px 12px; font-size: 13px; border-bottom: 1px solid #e2e8f0; }
        td:nth-child(2), td:nth-child(3), td:nth-child(4) { text-align: right; }
        tr:nth-child(even) { background: #f8fafc; }
        .totals { display: flex; justify-content: flex-end; margin-bottom: 20px; }
        .totals-box { width: 260px; }
        .total-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; }
        .total-final { background: #1a3a5c; color: white; padding: 12px 16px; border-radius: 6px; display: flex; justify-content: space-between; align-items: center; margin-top: 8px; }
        .total-final .amount { font-size: 22px; font-weight: bold; }
        .bank { background: #f8fafc; padding: 16px; border-radius: 6px; margin-bottom: 20px; font-size: 12px; }
        .footer { text-align: center; padding: 20px; font-size: 10px; color: #94a3b8; border-top: 1px solid #e2e8f0; }
        .copyright { font-size: 9px; color: #cbd5e1; margin-top: 8px; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style></head><body>${printContent.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  return (
    <div className="p-4 sm:p-8" data-testid="factures-page">
      <div className="mb-6">
        <h1 className="font-barlow text-2xl font-bold text-slate-900 tracking-tight uppercase">Factures</h1>
        <p className="text-sm text-slate-500 mt-1">Les factures sont générées automatiquement quand un bon de commande est terminé</p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher par numéro, client..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9" data-testid="factures-search" />
        </div>
        <Button variant="outline" onClick={() => { resetImport(); setImportDialogOpen(true); }} className="gap-2" data-testid="import-facture-btn">
          <Upload className="w-4 h-4" /> Importer PDF
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Chargement...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-400">Aucune facture pour le moment</p>
              <p className="text-xs text-slate-300 mt-1">Les factures apparaitront quand vous marquerez des commandes comme terminées</p>
            </div>
          ) : (
            <div className="overflow-x-auto"><Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold">N. Facture</TableHead>
                  <TableHead className="text-xs font-semibold">N. Commande</TableHead>
                  <TableHead className="text-xs font-semibold">Client</TableHead>
                  <TableHead className="text-xs font-semibold">Date</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Montant</TableHead>
                  <TableHead className="text-xs font-semibold">Statut</TableHead>
                  <TableHead className="text-xs font-semibold w-16">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(f => (
                  <TableRow key={f.facture_id} className="table-row-hover" data-testid={`facture-row-${f.facture_id}`}>
                    <TableCell className="text-sm font-medium text-slate-900">{f.facture_number}</TableCell>
                    <TableCell className="text-sm text-slate-500">{f.commande_number}</TableCell>
                    <TableCell className="text-sm text-slate-600">{f.client_name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(f.created_at)}</TableCell>
                    <TableCell className="text-sm text-right font-semibold text-slate-900">{f.total?.toLocaleString("fr-FR")} EUR</TableCell>
                    <TableCell>
                      <Select value={f.status} onValueChange={(v) => updateStatus(f.facture_id, v)}>
                        <SelectTrigger className="h-7 text-xs w-28 border-0" data-testid={`facture-status-${f.facture_id}`}>
                          <Badge className={`${statusColors[f.status]} text-[11px] font-medium border-0`}>{statusLabels[f.status]}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="emise">Émise</SelectItem>
                          <SelectItem value="payee">Payée</SelectItem>
                          <SelectItem value="annulee">Annulée</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => viewFacture(f)} className="h-8 w-8 p-0" data-testid={`view-facture-${f.facture_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(f.facture_id, f.facture_number)} className="h-8 w-8 p-0" data-testid={`pdf-facture-${f.facture_id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => sendFactureEmail(f.facture_id)} className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700" title="Envoyer par email" data-testid={`send-facture-email-${f.facture_id}`}>
                        <Mail className="w-4 h-4" />
                      </Button>
                      {isWebmaster && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteFacture(f.facture_id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-600" title="Supprimer" data-testid={`delete-facture-${f.facture_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl uppercase tracking-tight">Facture N. {selectedFacture?.facture_number}</DialogTitle>
            <DialogDescription>Commande {selectedFacture?.commande_number} - Devis {selectedFacture?.devis_number}</DialogDescription>
          </DialogHeader>
          {selectedFacture && (
            <>
              <div id="facture-print-content">
                <div className="header" style={{background:"#1a3a5c",color:"white",padding:"24px",borderRadius:"8px",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{display:"flex",gap:"12px",alignItems:"center"}}>
                    <div style={{width:"60px",height:"60px",background:"white",borderRadius:"8px",padding:"3px"}}>
                      <img src="/jvc-logo.png" alt="JVC" style={{width:"100%",height:"100%",objectFit:"contain"}} />
                    </div>
                    <div>
                      <div style={{fontSize:"18px",fontWeight:"bold",textTransform:"uppercase"}}>{selectedFacture.company?.name || "SAS J.V.C"}</div>
                      <div style={{fontSize:"11px",color:"#93c5fd",marginTop:"2px"}}>{selectedFacture.company?.activities}</div>
                      <div style={{fontSize:"10px",color:"#93c5fd",marginTop:"6px"}}>{selectedFacture.company?.address}, {selectedFacture.company?.postal_code} {selectedFacture.company?.city}</div>
                      <div style={{fontSize:"10px",color:"#93c5fd"}}>Tel: {selectedFacture.company?.phone} | Mobile: {selectedFacture.company?.mobile}</div>
                      <div style={{fontSize:"10px",color:"#93c5fd"}}>Email: {selectedFacture.company?.email}</div>
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:"24px",fontWeight:"bold",textTransform:"uppercase"}}>FACTURE</div>
                    <div style={{fontSize:"14px",color:"#bfdbfe",marginTop:"4px"}}>{selectedFacture.facture_number}</div>
                    <div style={{fontSize:"11px",color:"#93c5fd",marginTop:"8px"}}>Date: {formatDate(selectedFacture.created_at)}</div>
                  </div>
                </div>

                <div style={{padding:"24px 0"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"16px",marginBottom:"20px"}}>
                    <div style={{background:"#f8fafc",padding:"14px",borderRadius:"6px"}}>
                      <div style={{fontSize:"10px",color:"#94a3b8",textTransform:"uppercase",fontWeight:"600",marginBottom:"6px"}}>Facturé à</div>
                      <div style={{fontSize:"15px",fontWeight:"600"}}>{selectedFacture.client_name}</div>
                      <div style={{fontSize:"12px",color:"#64748b"}}>{selectedFacture.client_email}</div>
                      {selectedFacture.client_address && <div style={{fontSize:"12px",color:"#64748b"}}>{selectedFacture.client_address}</div>}
                      {selectedFacture.client_city && <div style={{fontSize:"12px",color:"#64748b"}}>{selectedFacture.client_postal_code} {selectedFacture.client_city}</div>}
                      {selectedFacture.client_siret && <div style={{fontSize:"11px",color:"#94a3b8"}}>SIRET: {selectedFacture.client_siret}</div>}
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:"12px",color:"#64748b"}}>Réf. Commande: {selectedFacture.commande_number}</div>
                      <div style={{fontSize:"12px",color:"#64748b"}}>Réf. Devis: {selectedFacture.devis_number}</div>
                      {selectedFacture.paid_at && <div style={{fontSize:"12px",color:"#16a34a",fontWeight:"600",marginTop:"6px"}}>Payée le {formatDate(selectedFacture.paid_at)}</div>}
                    </div>
                  </div>

                  {selectedFacture.objet && (
                    <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",padding:"10px 14px",borderRadius:"6px",marginBottom:"16px"}}>
                      <div style={{fontSize:"10px",color:"#3b82f6",textTransform:"uppercase",fontWeight:"600"}}>Objet</div>
                      <div style={{fontSize:"13px",fontWeight:"500",marginTop:"2px"}}>{selectedFacture.objet}</div>
                    </div>
                  )}

                  <div className="overflow-x-auto"><Table>
                    <TableHeader>
                      <TableRow className="bg-[#1a3a5c]">
                        <TableHead className="text-white text-xs font-semibold">Description</TableHead>
                        <TableHead className="text-white text-xs font-semibold text-center">Qté</TableHead>
                        <TableHead className="text-white text-xs font-semibold text-right">Prix unitaire</TableHead>
                        <TableHead className="text-white text-xs font-semibold text-right">Montant net</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedFacture.items?.map((item, idx) => (
                        <TableRow key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                          <TableCell className="text-sm">{item.description}</TableCell>
                          <TableCell className="text-sm text-center">{item.quantity} {item.unit}</TableCell>
                          <TableCell className="text-sm text-right">{item.unit_price?.toFixed(2)} EUR</TableCell>
                          <TableCell className="text-sm text-right font-medium">{item.total?.toFixed(2)} EUR</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table></div>

                  <div style={{display:"flex",justifyContent:"flex-end",marginTop:"16px"}}>
                    <div style={{width:"260px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:"13px"}}>
                        <span style={{color:"#64748b"}}>Total net</span>
                        <span style={{fontWeight:"500"}}>{selectedFacture.subtotal?.toFixed(2)} EUR</span>
                      </div>
                      {selectedFacture.tax_rate > 0 && (
                        <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:"13px"}}>
                          <span style={{color:"#64748b"}}>TVA ({selectedFacture.tax_rate}%)</span>
                          <span style={{fontWeight:"500"}}>{selectedFacture.tax_amount?.toFixed(2)} EUR</span>
                        </div>
                      )}
                      <div style={{background:"#1a3a5c",color:"white",padding:"12px 16px",borderRadius:"6px",display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:"8px"}}>
                        <span style={{fontWeight:"600",fontSize:"13px"}}>NET A PAYER</span>
                        <span style={{fontSize:"20px",fontWeight:"bold"}}>{selectedFacture.total?.toFixed(2)} EUR</span>
                      </div>
                    </div>
                  </div>

                  <div style={{background:"#f8fafc",padding:"14px",borderRadius:"6px",marginTop:"20px",fontSize:"12px"}}>
                    <div style={{fontSize:"10px",color:"#94a3b8",textTransform:"uppercase",fontWeight:"600",marginBottom:"6px"}}>Coordonnées bancaires</div>
                    <div style={{color:"#475569"}}><strong>IBAN:</strong> {selectedFacture.company?.iban}</div>
                    <div style={{color:"#475569"}}><strong>BIC:</strong> {selectedFacture.company?.bic}</div>
                  </div>

                  <div style={{textAlign:"center",paddingTop:"20px",fontSize:"10px",color:"#94a3b8",borderTop:"1px solid #e2e8f0",marginTop:"20px"}}>
                    <div>{selectedFacture.company?.name} - SIRET: {selectedFacture.company?.siret} | {selectedFacture.company?.address}, {selectedFacture.company?.postal_code} {selectedFacture.company?.city}</div>
                    <div style={{fontSize:"9px",color:"#cbd5e1",marginTop:"6px"}}>CopyRight by De La Rosa Design</div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t">
                <Button onClick={printFacture} variant="outline" data-testid="print-facture-btn">
                  <Printer className="w-4 h-4 mr-2" /> Imprimer / PDF
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Facture Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => { if (!open) { resetImport(); } setImportDialogOpen(open); }}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-barlow text-lg uppercase tracking-tight flex items-center gap-2">
              <Upload className="w-5 h-5" /> Importer une facture PDF
            </DialogTitle>
            <DialogDescription>
              Uploadez une ancienne facture PDF — l'IA analysera le contenu et créera la facture, le BC et l'intervention (terminés)
            </DialogDescription>
          </DialogHeader>

          {importStep === "upload" && (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-slate-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="import-facture-drop-zone"
              >
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-600">{importFile ? importFile.name : "Cliquez pour sélectionner un PDF"}</p>
                <p className="text-xs text-slate-400 mt-1">La facture sera analysée par IA pour extraire les données</p>
                <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileSelect} data-testid="import-facture-file-input" />
              </div>
              {importFile && (
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-md">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-800">{importFile.name}</span>
                  </div>
                  <Button onClick={handleAnalyze} className="bg-orange-600 hover:bg-orange-700 gap-2" data-testid="analyze-facture-pdf-btn">
                    <Search className="w-4 h-4" /> Analyser
                  </Button>
                </div>
              )}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 space-y-1">
                <p className="font-semibold">Important :</p>
                <p>- La référence et la date de la facture originale seront conservées</p>
                <p>- Aucun email ne sera envoyé au client</p>
                <p>- Le client sera créé automatiquement s'il n'existe pas</p>
                <p className="font-semibold text-orange-700">- Le BC et l'intervention seront marqués comme "Terminés"</p>
              </div>
            </div>
          )}

          {importStep === "analyzing" && (
            <div className="py-12 text-center space-y-4">
              <Loader2 className="w-10 h-10 text-orange-500 animate-spin mx-auto" />
              <div>
                <p className="text-sm font-medium text-slate-700">Analyse du PDF en cours...</p>
                <p className="text-xs text-slate-400 mt-1">L'IA extrait les informations de la facture</p>
              </div>
            </div>
          )}

          {importStep === "preview" && extractedData && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <p className="text-sm font-medium text-emerald-800">Données extraites avec succès</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-md">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Référence</p>
                  <Input value={extractedData.reference || ""} onChange={e => setExtractedData(d => ({ ...d, reference: e.target.value }))} className="h-8 text-sm font-bold" data-testid="import-facture-ref-input" />
                </div>
                <div className="p-3 bg-slate-50 rounded-md">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Date</p>
                  <Input type="date" value={extractedData.date || ""} onChange={e => setExtractedData(d => ({ ...d, date: e.target.value }))} className="h-8 text-sm" data-testid="import-facture-date-input" />
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-md space-y-2">
                <p className="text-[10px] text-slate-400 uppercase font-semibold">Client</p>
                <Input value={extractedData.client?.company_name || ""} onChange={e => setExtractedData(d => ({ ...d, client: { ...d.client, company_name: e.target.value } }))} placeholder="Nom entreprise" className="h-8 text-sm font-semibold" data-testid="import-fac-client-name" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={extractedData.client?.contact_name || ""} onChange={e => setExtractedData(d => ({ ...d, client: { ...d.client, contact_name: e.target.value } }))} placeholder="Contact" className="h-7 text-xs" data-testid="import-fac-client-contact" />
                  <Input value={extractedData.client?.email || ""} onChange={e => setExtractedData(d => ({ ...d, client: { ...d.client, email: e.target.value } }))} placeholder="Email" className="h-7 text-xs" data-testid="import-fac-client-email" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={extractedData.client?.address || ""} onChange={e => setExtractedData(d => ({ ...d, client: { ...d.client, address: e.target.value } }))} placeholder="Adresse" className="h-7 text-xs" data-testid="import-fac-client-address" />
                  <Input value={extractedData.client?.city || ""} onChange={e => setExtractedData(d => ({ ...d, client: { ...d.client, city: e.target.value } }))} placeholder="Ville" className="h-7 text-xs" data-testid="import-fac-client-city" />
                </div>
              </div>

              <div className="p-3 bg-orange-50 rounded-md">
                <p className="text-[10px] text-orange-400 uppercase font-semibold mb-1">Objet</p>
                <Input value={extractedData.objet || ""} onChange={e => setExtractedData(d => ({ ...d, objet: e.target.value }))} placeholder="Objet de la facture" className="h-8 text-sm bg-white" data-testid="import-fac-objet" />
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Lignes ({extractedData.items?.length || 0})</p>
                <div className="border rounded-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-slate-800 text-white">
                        <th className="p-2 text-left">Description</th>
                        <th className="p-2 text-center">Qté</th>
                        <th className="p-2 text-right">P.U.</th>
                        <th className="p-2 text-right">Total</th>
                      </tr></thead>
                      <tbody>
                        {extractedData.items?.map((item, idx) => (
                          item.item_type === "title" ? (
                            <tr key={idx} className="bg-slate-700 text-white font-bold">
                              <td colSpan={4} className="p-2 uppercase">{item.description}</td>
                            </tr>
                          ) : (
                            <tr key={idx} className={idx % 2 ? "bg-slate-50" : ""}>
                              <td className="p-2">{item.description}</td>
                              <td className="p-2 text-center">{item.quantity} {item.unit}</td>
                              <td className="p-2 text-right">{item.unit_price?.toFixed(2)}</td>
                              <td className="p-2 text-right font-medium">{(item.quantity * item.unit_price).toFixed(2)}</td>
                            </tr>
                          )
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-900 text-white rounded-md">
                <span className="font-semibold text-sm">TOTAL</span>
                <span className="text-lg font-bold font-barlow">{extractedData.total?.toFixed(2)} EUR</span>
              </div>

              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" onClick={resetImport}>Recommencer</Button>
                <Button onClick={handleConfirmImport} className="bg-orange-600 hover:bg-orange-700 gap-2" data-testid="confirm-import-facture-btn">
                  <CheckCircle className="w-4 h-4" /> Confirmer l'import
                </Button>
              </div>
            </div>
          )}

          {importStep === "confirming" && (
            <div className="py-12 text-center space-y-4">
              <Loader2 className="w-10 h-10 text-orange-500 animate-spin mx-auto" />
              <p className="text-sm font-medium text-slate-700">Création de la facture, BC et intervention...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
